/**
 * The route table (spec §4.1). Public routes, then one protected layout that
 * carries the shell and every feature's routes from the static registry.
 *
 * Foundation file — features add routes through `src/features/<id>/index.ts`.
 */
import { CourseStartPage } from "@client/pages/CourseStart";
import { Navigate, Outlet, createBrowserRouter, useLocation, type RouteObject } from "react-router";
import { authLink, returnToFromLocation } from "@client/lib/returnTo";
import type { Role } from "@shared/api";
import { Spinner } from "@client/components/ui";
import { useT } from "@client/i18n";
import { HomePage } from "@client/pages/Home";
import { LandingPage } from "@client/pages/Landing";
import { LoginPage } from "@client/pages/Login";
import { NotFoundPage } from "@client/pages/NotFound";
import { RegisterGooglePage } from "@client/pages/RegisterGoogle";
import { PrivacyPage } from "@client/pages/Privacy";
import { HelpPage } from "@client/pages/Help";
import { SettingsPage } from "@client/pages/Settings";
import { AppShell } from "./AppShell";
import { useAuth } from "./auth";
import { features } from "./features";
import type { FeatureModule } from "./types";

function FullPageSpinner() {
  const t = useT();
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Spinner size="lg" label={t("common.loading")} />
    </div>
  );
}

/** Signed-in layout: the shell plus the child route. */
export function RequireAuth() {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  if (isLoading) return <FullPageSpinner />;
  if (!user)
    return (
      <Navigate
        to={authLink("login", location.pathname + location.search + location.hash)}
        replace
      />
    );
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

/** Role guard — the teacher feature declares `requiresRole: "teacher"`. */
export function RequireRole({ role }: { role: Role }) {
  const { user, isLoading } = useAuth();
  if (isLoading) return <FullPageSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== role) return <Navigate to="/home" replace />;
  return <Outlet />;
}

/** Feature routes, each wrapped in its role guard when it declares one. */
export function featureRoutes(list: readonly FeatureModule[] = features): RouteObject[] {
  return list.flatMap((feature) =>
    feature.requiresRole
      ? [{ element: <RequireRole role={feature.requiresRole} />, children: feature.routes }]
      : feature.routes,
  );
}

function RegisterRedirect() {
  const location = useLocation();
  return <Navigate to={authLink("login", returnToFromLocation(location))} replace />;
}

export const appRoutes: RouteObject[] = [
  { path: "/", element: <LandingPage /> },
  { path: "/start", element: <CourseStartPage /> },
  { path: "/integritet", element: <PrivacyPage /> },
  { path: "/privacy", element: <Navigate to="/integritet" replace /> },
  { path: "/login", element: <LoginPage /> },
  { path: "/register", element: <RegisterRedirect /> },
  { path: "/register/google", element: <RegisterGooglePage /> },
  {
    element: <RequireAuth />,
    children: [
      { path: "/home", element: <HomePage /> },
      { path: "/settings", element: <SettingsPage /> },
      { path: "/hjalp", element: <HelpPage /> },
      ...featureRoutes(),
    ],
  },
  { path: "*", element: <NotFoundPage /> },
];

export function createRouter() {
  return createBrowserRouter(appRoutes);
}
